/**
 * The checkpoints' names, for the public page (components/profile/PublicProfile.tsx).
 *
 * This file used to hold a whole panel as well — "Server verification", with
 * a button of its own beside "Check my work". It is gone: a checkpoint is
 * checked by "Check my work" like every other level, and what that says lives
 * in strings/checkpoint.ts, none of which names a server.
 */
export const en = {
  /**
   * The pilot levels, named.
   *
   * Here rather than read from the mission catalogue, because the public page
   * (components/profile/PublicProfile.tsx) draws these names and never loads a
   * catalogue: it is opened by strangers, and missions.json is 230 KB of level
   * specifications that a page showing three badges has no business
   * downloading. The Worker cannot supply them either — it has no mission list
   * by design (docs/contracts.md, "The record behind the tag") — and a name it
   * did send would arrive in English in an Arabic page.
   *
   * `tsc` keeps the two languages in step because these are literal keys. What
   * it cannot check is that they are the same three levels the verifier
   * actually grades, so web/test/verification.test.ts does: every id in
   * `VERIFICATION_MISSIONS` has a name in both languages, and no name here
   * belongs to a level the pilot has dropped.
   */
  missions: {
    'cp-first-commits': 'Checkpoint: First Commits',
    'cp-remotes': 'Checkpoint: Branches & Remotes',
    'cp-recovery': 'Checkpoint: Recovery',
    'cp-quality': 'Checkpoint: Quality',
    'cp-team-git': 'Checkpoint: Team Git',
    'cp-code-review': 'Checkpoint: Code Review',
    'cp-the-codebase': 'Checkpoint: The Codebase',
    'cp-releases': 'Checkpoint: Releases',
    'cp-production': 'Checkpoint: Production',
    'cp-pipeline': 'Checkpoint: The Pipeline',
    'cp-data': 'Checkpoint: Data',
    'cp-design': 'Checkpoint: Design',
    'cp-ownership': 'Checkpoint: Ownership',
    'cp-network': 'Checkpoint: The Network',
    'cp-database': 'Checkpoint: The Database',
    'cp-api': 'Checkpoint: The API',
    'cp-security': 'Checkpoint: Security',
    'cp-concurrency': 'Checkpoint: Concurrency',
    'cp-observability': 'Checkpoint: Observability',
    'cp-scale-and-cost': 'Checkpoint: Scale and cost',
    'cp-legacy': 'Checkpoint: Legacy',
    'cp-configuration': 'Checkpoint: Configuration',
    'cp-craft': 'Checkpoint: The Craft',
    'cp-engineer': 'Checkpoint: The Engineer',
    'cp-whole-job': 'Checkpoint: The Whole Job',
    'cp-agent-review': "Checkpoint: The agent's pull request",
  },
};

export const ar: typeof en = {
  /* The titles as each level's own `mission.ar.yaml` writes them, down to
     leaving `commit` in Latin script, which is the glossary's rule for a Git
     term a learner types (docs/i18n.md). */
  missions: {
    'cp-first-commits': 'محطة: أول commits',
    'cp-remotes': 'محطة: الفروع والمستودعات البعيدة',
    'cp-recovery': 'محطة: الاستعادة',
    'cp-quality': 'محطة: الجودة',
    'cp-team-git': 'محطة: العمل على Git مع فريق',
    'cp-code-review': 'محطة: مراجعة الشيفرة',
    'cp-the-codebase': 'محطة: قاعدة الشيفرة',
    'cp-releases': 'محطة: الإصدارات',
    'cp-production': 'محطة: الإنتاج',
    'cp-pipeline': 'محطة: خطّ الإنتاج',
    'cp-data': 'محطة: البيانات',
    'cp-design': 'محطة: التصميم',
    'cp-ownership': 'محطة: الملكيّة',
    'cp-network': 'محطة: الشبكة',
    'cp-database': 'محطة: قاعدة البيانات',
    'cp-api': 'محطة: الواجهة البرمجية',
    'cp-security': 'محطة: الأمان',
    'cp-concurrency': 'محطة: التزامن',
    'cp-observability': 'محطة: القابليّة للرصد',
    'cp-scale-and-cost': 'محطة: الحجم والكلفة',
    'cp-legacy': 'محطة: الشيفرة الموروثة',
    'cp-configuration': 'محطة: الإعداد',
    'cp-craft': 'محطة: الصنعة',
    'cp-engineer': 'محطة: المهندس',
    'cp-whole-job': 'محطة: العمل كلّه',
    'cp-agent-review': 'محطة: الـ pull request الذي فتحه الوكيل',
  },
};

export const de: typeof en = {
  missions: {
    'cp-first-commits': 'Checkpoint: Erste Commits',
    'cp-remotes': 'Checkpoint: Branches & Remotes',
    'cp-recovery': 'Checkpoint: Wiederherstellung',
    'cp-quality': 'Checkpoint: Qualität',
    'cp-team-git': 'Checkpoint: Git im Team',
    'cp-code-review': 'Checkpoint: Code-Review',
    'cp-the-codebase': 'Checkpoint: Die Codebasis',
    'cp-releases': 'Checkpoint: Releases',
    'cp-production': 'Checkpoint: Produktion',
    'cp-pipeline': 'Checkpoint: Die Pipeline',
    'cp-data': 'Checkpoint: Daten',
    'cp-design': 'Checkpoint: Design',
    'cp-ownership': 'Checkpoint: Verantwortung',
    'cp-network': 'Checkpoint: Das Netzwerk',
    'cp-database': 'Checkpoint: Die Datenbank',
    'cp-api': 'Checkpoint: Die API',
    'cp-security': 'Checkpoint: Sicherheit',
    'cp-concurrency': 'Checkpoint: Nebenläufigkeit',
    'cp-observability': 'Checkpoint: Beobachtbarkeit',
    'cp-scale-and-cost': 'Checkpoint: Skalierung und Kosten',
    'cp-legacy': 'Checkpoint: Altsysteme',
    'cp-configuration': 'Checkpoint: Konfiguration',
    'cp-craft': 'Checkpoint: Das Handwerk',
    'cp-engineer': 'Checkpoint: Der Engineer',
    'cp-whole-job': 'Checkpoint: Der ganze Job',
    'cp-agent-review': 'Checkpoint: Der Pull Request des Agenten',
  },
};

export const ja: typeof en = {
  missions: {
    'cp-first-commits': 'チェックポイント: はじめてのコミット',
    'cp-remotes': 'チェックポイント: ブランチとリモート',
    'cp-recovery': 'チェックポイント: リカバリー',
    'cp-quality': 'チェックポイント: 品質',
    'cp-team-git': 'チェックポイント: チームでのGit',
    'cp-code-review': 'チェックポイント: コードレビュー',
    'cp-the-codebase': 'チェックポイント: コードベース',
    'cp-releases': 'チェックポイント: リリース',
    'cp-production': 'チェックポイント: 本番環境',
    'cp-pipeline': 'チェックポイント: パイプライン',
    'cp-data': 'チェックポイント: データ',
    'cp-design': 'チェックポイント: 設計',
    'cp-ownership': 'チェックポイント: オーナーシップ',
    'cp-network': 'チェックポイント: ネットワーク',
    'cp-database': 'チェックポイント: データベース',
    'cp-api': 'チェックポイント: API',
    'cp-security': 'チェックポイント: セキュリティ',
    'cp-concurrency': 'チェックポイント: 並行処理',
    'cp-observability': 'チェックポイント: オブザーバビリティ',
    'cp-scale-and-cost': 'チェックポイント: スケールとコスト',
    'cp-legacy': 'チェックポイント: レガシー',
    'cp-configuration': 'チェックポイント: 設定管理',
    'cp-craft': 'チェックポイント: 仕事の流儀',
    'cp-engineer': 'チェックポイント: エンジニア',
    'cp-whole-job': 'チェックポイント: 仕事まるごと',
    'cp-agent-review': 'チェックポイント: エージェントのプルリクエスト',
  },
};
